import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

/**
 * Minimal AngularJS-`$http`-shaped Promise adapter over the native HttpClient, for the
 * account-v2 redux thunks the NgRx strangler facades reuse VERBATIM (they call
 * `$http.get(...).then(response => response.data)` with `${environment.apiBaseUrl}` URLs).
 * Resolves `{ data, status }` (`.data` is the only field the thunks read; on error the
 * object is passed opaquely into reducers) and rejects with `{ data, status }` mapped
 * from the HttpErrorResponse (the v1 $http rejection's read fields).
 *
 * Scope: thunk extra-args ONLY. The v1 domain services get real native HttpClient ports
 * (LayersApiService/PhotoContestApiService/PropertyApiService/GuestsV1ApiService — the
 * locked WS5 #10 decision); this shim is not a vehicle for porting those. It exposes
 * exactly the verbs the account-v2 thunks use (get ×25 / post ×3 / put ×2, census
 * 2026-06-10).
 *
 * WS5 #10 Stage A inc 2e: lets the story-modal-launcher (and later the cooperative
 * facade) build a fully-native thunk extras object instead of `$injector.get('$http')`.
 */
@Injectable({ providedIn: 'root' })
export class V1HttpShimService {
  private readonly http = inject(HttpClient);

  get(url: string): Promise<{ data: any; status: number }> {
    return this.request('GET', url);
  }

  post(url: string, body?: any): Promise<{ data: any; status: number }> {
    return this.request('POST', url, body);
  }

  put(url: string, body?: any): Promise<{ data: any; status: number }> {
    return this.request('PUT', url, body);
  }

  private request(method: string, url: string, body?: any): Promise<{ data: any; status: number }> {
    return firstValueFrom(this.http.request<any>(method, url, { body, observe: 'response' })).then(
      (response) => ({ data: response.body, status: response.status }),
      (error) => {
        throw { data: error?.error, status: error?.status };
      },
    );
  }
}
